/**
 * Trade Import Configuration Panel (KAN-259)
 *
 * Module-agnostic admin component for managing trade import configurations.
 * Each config defines how to map columns from an external source (CSV/Excel)
 * into the target module's trade schema.
 *
 * Props:
 *   module — "Commodities" | "FX" — determines which target fields are shown.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { useAuth } from '../auth/AuthContext';
import { tradeImportService, TradeImportConfig, ColumnMapping, ConditionalFieldEntry, ConditionalFieldBranch, ConditionalFieldCondition, ConditionalFieldOutput, ComputedFieldEntry, ComputedFieldPart, ExtractType } from '../../services/api/tradeImportService';
import { clientService } from '../../services/api/clientService';
import { masterDataService } from '../../services/api/masterDataService';
import { getFieldsForModule, getEnumsForModule } from '../../constants/tradeFields';
import type { ValueTransform } from '../../types/tradeImageCapture';
import AlertModal from '../common/AlertModal';
import TextReplaceBuilder from '../outputFormats/TextReplaceBuilder';
import './TradeImportConfigPanel.css';

const TRANSFORM_TYPE_KEYS = [
  'DATE_FORMAT',
  'ENUM_MAP',
  'NUMBER_FORMAT',
  'TEXT_REPLACE',
  'TEXT_TRANSFORM',
] as const;

const CONDITIONAL_OPERATORS = [
  { value: 'EQUALS', labelKey: 'commodities.tradeImport.conditional.equals' },
  { value: 'NOT_EQUALS', labelKey: 'commodities.tradeImport.conditional.notEquals' },
  { value: 'IS_NULL', labelKey: 'commodities.tradeImport.conditional.isNull' },
  { value: 'IS_NOT_NULL', labelKey: 'commodities.tradeImport.conditional.isNotNull' },
  { value: 'GREATER_THAN', labelKey: 'commodities.tradeImport.conditional.greaterThan' },
  { value: 'LESS_THAN', labelKey: 'commodities.tradeImport.conditional.lessThan' },
] as const;

const CONDITIONAL_OUTPUT_TYPES = [
  { value: 'STATIC', labelKey: 'commodities.tradeImport.conditional.static' },
  { value: 'FIELD', labelKey: 'commodities.tradeImport.conditional.field' },
] as const;

const SOURCE_TYPES = [
  { value: 'CSV', label: 'CSV' },
  { value: 'EXCEL', label: 'Excel' },
];

const EXTRACT_TYPES = [
  { value: 'VALUE', labelKey: 'commodities.tradeImport.computed.extractValue' },
  { value: 'MONTH', labelKey: 'commodities.tradeImport.computed.extractMonth' },
  { value: 'YEAR_2D', labelKey: 'commodities.tradeImport.computed.extractYear2d' },
  { value: 'YEAR_4D', labelKey: 'commodities.tradeImport.computed.extractYear4d' },
  { value: 'DAY', labelKey: 'commodities.tradeImport.computed.extractDay' },
  { value: 'FIRST', labelKey: 'commodities.tradeImport.computed.extractFirst' },
  { value: 'LAST', labelKey: 'commodities.tradeImport.computed.extractLast' },
] as const;

const DATE_EXTRACT_TYPES = new Set(['MONTH', 'YEAR_2D', 'YEAR_4D', 'DAY']);
const LENGTH_EXTRACT_TYPES = new Set(['FIRST', 'LAST']);

// Module-level config for dynamic reference data loading.
// Add a new entry when onboarding a future asset-class module.
const MODULE_REF_DATA_CONFIG: Record<string, {
  counterpartyType: 'fx' | 'commodity';
  assetClass?: string;         // if set, fetch underlyings filtered to this class
  loadMasterData: boolean;     // whether to fetch underlyings/venues/exchange contracts
}> = {
  Commodities: { counterpartyType: 'commodity', assetClass: 'Commodity', loadMasterData: true },
  FX:          { counterpartyType: 'fx',        loadMasterData: false },
};

const LOOKUP_FIELD_OPTIONS = [
  { value: 'LegacyReference1', labelKey: 'commodities.expiryImport.lookupFieldLegacyRef1' },
  { value: 'LegacyReference2', labelKey: 'commodities.expiryImport.lookupFieldLegacyRef2' },
  { value: 'TradeNumber', labelKey: 'commodities.expiryImport.lookupFieldTradeNumber' },
];

// Fields that may be mapped in an Expiry config — no regular trade fields
const EXPIRY_MAPPABLE_FIELDS = new Set([
  'LegacyReference1',
  'LegacyReference2',
  'TradeNumber',
  'SettlementPrice',
  'SettlementAmount',
  'PaymentDate',
]);

interface Props {
  module: string; // 'Commodities' | 'FX'
  importContext?: 'Subscription' | 'Expiry'; // default 'Subscription'
}

type PanelMode = 'loading' | 'list' | 'create' | 'edit';

const TradeImportConfigPanel: React.FC<Props> = ({ module, importContext = 'Subscription' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Panel mode
  const [mode, setMode] = useState<PanelMode>('loading');

  // Config list
  const [configs, setConfigs] = useState<TradeImportConfig[]>([]);

  // Currently editing config
  const [editingConfig, setEditingConfig] = useState<TradeImportConfig | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<'CSV' | 'EXCEL'>('CSV');
  const [startRow, setStartRow] = useState(1);
  const [startColumn, setStartColumn] = useState('');
  const [includesHeaders, setIncludesHeaders] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [valueTransforms, setValueTransforms] = useState<ValueTransform[]>([]);
  const [conditionalFields, setConditionalFields] = useState<ConditionalFieldEntry[]>([]);
  const [computedFields, setComputedFields] = useState<ComputedFieldEntry[]>([]);
  const [skipRules, setSkipRules] = useState<{ conditions: ConditionalFieldCondition[] }[]>([]);
  const [defaultValues, setDefaultValues] = useState<{ field: string; value: string }[]>([]);
  const [deferredFields, setDeferredFields] = useState<string[]>([]);
  const [lookupField, setLookupField] = useState<string>('LegacyReference1');

  // Sample file headers (extracted from uploaded CSV/Excel)
  const [sampleHeaders, setSampleHeaders] = useState<string[]>([]);
  const [sampleFileName, setSampleFileName] = useState<string>('');
  const sampleFileRef = useRef<HTMLInputElement>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Expanded transform config row
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  // Expanded conditional field entry (-1 = none, index = which entry is open)
  const [expandedCFIndex, setExpandedCFIndex] = useState<number | null>(null);
  const [expandedCompIndex, setExpandedCompIndex] = useState<number | null>(null);

  // New default value inputs

  // Dynamic enum data from APIs
  const [dynamicEnums, setDynamicEnums] = useState<Record<string, { value: string; label: string }[]>>({});

  // Build target field options from module registry
  // For Expiry configs, restrict to settlement-specific fields only so regular
  // trade fields (Price, Lots, MaturityDate, etc.) cannot be overwritten.
  const targetFields = useMemo(() => {
    const all = getFieldsForModule(module).map((f) => ({
      value: f.field,
      label: t(f.labelKey),
      calculated: f.calculated || false,
    }));
    if (importContext === 'Expiry') {
      // All fields in EXPIRY_MAPPABLE_FIELDS are directly importable from the broker file
      // regardless of their calculated status in the subscription registry.
      return all
        .filter((f) => EXPIRY_MAPPABLE_FIELDS.has(f.value))
        .map((f) => ({ ...f, calculated: false }));
    }
    return all;
  }, [module, importContext, t]);

  // Build enum values for target fields (static + dynamic)
  const fieldEnums = useMemo(() => {
    const enums: Record<string, { value: string; label: string }[]> = {};
    for (const [field, values] of Object.entries(getEnumsForModule(module))) {
      enums[field] = values.map((v) => ({ value: v.value, label: t(v.labelKey) }));
    }
    // Merge dynamic enums (counterparty names, underlyings, venues, etc.)
    for (const [field, values] of Object.entries(dynamicEnums)) {
      enums[field] = values;
    }
    return enums;
  }, [module, t, dynamicEnums]);

  // Target fields available for column mapping (excludes those claimed by conditional fields)
  const mappableTargetFields = useMemo(() => {
    const cfTargets = new Set(conditionalFields.map((cf) => cf.targetField).filter(Boolean));
    const compTargets = new Set(computedFields.map((cf) => cf.targetField).filter(Boolean));
    return targetFields.filter((f) => !cfTargets.has(f.value) && !compTargets.has(f.value));
  }, [targetFields, conditionalFields, computedFields]);

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success'), message });
  };

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  // ------------------------------------------------------------------
  // Sample file parsing — extract headers from CSV/Excel
  // ------------------------------------------------------------------

  const handleSampleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();

    // Validate file extension matches selected source type
    if (sourceType === 'CSV' && ext !== 'csv') {
      showError(t('commodities.tradeImport.sampleTypeMismatch', { expected: 'CSV', got: `.${ext}` }));
      event.target.value = '';
      return;
    }
    if (sourceType === 'EXCEL' && ext !== 'xlsx' && ext !== 'xls') {
      showError(t('commodities.tradeImport.sampleTypeMismatch', { expected: 'Excel', got: `.${ext}` }));
      event.target.value = '';
      return;
    }

    if (ext === 'csv') {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        preview: 1, // only read the first row
        complete: (results) => {
          const firstRow = results.data?.[0] as string[] | undefined;
          if (firstRow && firstRow.length > 0) {
            const headers = firstRow.map((h) => String(h).trim()).filter(Boolean);
            setSampleHeaders(headers);
            setSampleFileName(file.name);
          } else {
            showError(t('commodities.tradeImport.sampleNoHeaders'));
          }
        },
        error: () => {
          showError(t('commodities.tradeImport.sampleParseError'));
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      file.arrayBuffer().then(async (buffer) => {
        try {
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
          const worksheet = workbook.worksheets[0];
          if (!worksheet || worksheet.rowCount < 1) {
            showError(t('commodities.tradeImport.sampleNoHeaders'));
            return;
          }
          const headerRow = worksheet.getRow(1);
          const headers: string[] = [];
          headerRow.eachCell({ includeEmpty: false }, (cell) => {
            const val = String(cell.value ?? '').trim();
            if (val) headers.push(val);
          });
          if (headers.length > 0) {
            setSampleHeaders(headers);
            setSampleFileName(file.name);
          } else {
            showError(t('commodities.tradeImport.sampleNoHeaders'));
          }
        } catch {
          showError(t('commodities.tradeImport.sampleParseError'));
        }
      });
    } else {
      showError(t('commodities.tradeImport.sampleUnsupportedType'));
    }

    // Reset input so the same file can be re-uploaded
    event.target.value = '';
  };

  const clearSampleHeaders = () => {
    setSampleHeaders([]);
    setSampleFileName('');
  };

  // ------------------------------------------------------------------
  // Load configs on mount
  // ------------------------------------------------------------------

  const loadConfigs = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await tradeImportService.listConfigs(clientId, module, importContext);
      setConfigs(result);
      setMode('list');
    } catch (err: any) {
      console.error('Failed to load import configs:', err);
      showError(err.message || 'Failed to load configurations');
      setMode('list');
    }
  }, [clientId, module, importContext]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // ------------------------------------------------------------------
  // Load dynamic enum data (counterparties, entities, underlyings, etc.)
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!clientId) return;

    const loadDynamicEnums = async () => {
      const enums: Record<string, { value: string; label: string }[]> = {};
      const refCfg = MODULE_REF_DATA_CONFIG[module];

      try {
        // Counterparty names (filtered by module type)
        const cpType = refCfg?.counterpartyType;
        const cpResponse = await clientService.getCounterpartyNames(clientId, cpType);
        if (cpResponse.data?.length) {
          enums.CounterpartyName = cpResponse.data.map((cp) => ({
            value: cp.counterpartyName,
            label: cp.counterpartyName,
          }));
        }
      } catch (err) {
        console.warn('Failed to load counterparty names:', err);
      }

      try {
        // Trading entities (OurParty + InternalCounterparty share the same entity list)
        const teResponse = await clientService.getTradingEntities(clientId);
        if (teResponse.data?.length) {
          const entityOptions = teResponse.data.map((te) => ({
            value: te.name,
            label: te.name,
          }));
          enums.OurParty = entityOptions;
          enums.InternalCounterparty = entityOptions;
        }
      } catch (err) {
        console.warn('Failed to load trading entities:', err);
      }

      // Master data (underlyings, venues, exchange contracts) — per module config
      if (refCfg?.loadMasterData) {
        try {
          const underlyings = await masterDataService.listUnderlyings(refCfg.assetClass);
          if (underlyings.length) {
            enums.UnderlyingCode = underlyings.map((u) => ({
              value: u.code,
              label: u.name,
            }));
          }
        } catch (err) {
          console.warn('Failed to load underlyings:', err);
        }

        try {
          const venues = await masterDataService.listVenues();
          if (venues.length) {
            enums.VenueCode = venues.map((v) => ({
              value: v.code,
              label: v.name,
            }));
          }
        } catch (err) {
          console.warn('Failed to load venues:', err);
        }

        try {
          const contracts = await masterDataService.listExchangeContracts();
          if (contracts.length) {
            enums.ExchangeContractCode = contracts.map((c) => ({
              value: c.code,
              label: c.name,
            }));
          }
        } catch (err) {
          console.warn('Failed to load exchange contracts:', err);
        }
      }

      setDynamicEnums(enums);
    };

    loadDynamicEnums();
  }, [clientId, module]);

  // ------------------------------------------------------------------
  // Reset form to blank (for create)
  // ------------------------------------------------------------------

  const resetForm = () => {
    setName('');
    setSourceType('CSV');
    setStartRow(1);
    setStartColumn('');
    setIncludesHeaders(true);
    setEnabled(true);
    setColumnMappings([]);
    setValueTransforms([]);
    setConditionalFields([]);
    setComputedFields([]);
    setSkipRules([]);
    setDefaultValues([]);
    setDeferredFields([]);
    setLookupField('LegacyReference1');
    setEditingId(null);
    setEditingConfig(null);
    setExpandedRowIndex(null);
    setExpandedCFIndex(null);
    setExpandedCompIndex(null);
    setSampleHeaders([]);
    setSampleFileName('');
  };

  // ------------------------------------------------------------------
  // Populate form from existing config (for edit)
  // ------------------------------------------------------------------

  const populateForm = (config: TradeImportConfig) => {
    setName(config.name);
    setSourceType(config.sourceType as 'CSV' | 'EXCEL');
    setStartRow(config.startRow || 1);
    setStartColumn(config.startColumn || '');
    setIncludesHeaders(config.includesHeaders !== false);
    setEnabled(config.enabled);
    setColumnMappings([...(config.columnMappings || [])]);
    setValueTransforms([...(config.valueTransforms || [])]);
    setConditionalFields([...(config.conditionalFields || [])]);
    setComputedFields([...(config.computedFields || [])]);
    setSkipRules([...(config.skipRules || [])]);
    setDefaultValues(
      Object.entries(config.defaultValues || {}).map(([field, value]) => ({ field, value }))
    );
    setDeferredFields([...(config.deferredFields || [])]);
    setLookupField(config.lookupField || 'LegacyReference1');
    setSampleHeaders(config.sampleHeaders || []);
    setEditingId(config.id || null);
    setEditingConfig(config);
  };

  // ------------------------------------------------------------------
  // Create / Edit handlers
  // ------------------------------------------------------------------

  const handleCreate = () => {
    resetForm();
    setMode('create');
  };

  const handleEdit = (config: TradeImportConfig) => {
    populateForm(config);
    setMode('edit');
  };

  const handleBackToList = () => {
    resetForm();
    setMode('list');
  };

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      showError(t('commodities.tradeImport.validationNameRequired'));
      return;
    }
    if (columnMappings.length === 0) {
      showError(t('commodities.tradeImport.validationMappingsRequired'));
      return;
    }
    if (importContext === 'Expiry') {
      const lookupMapped = columnMappings.some((m) => m.targetFields.includes(lookupField));
      if (!lookupMapped) {
        showError(t('commodities.expiryImport.lookupFieldNotMapped', { field: lookupField }));
        return;
      }
    }

    setSaving(true);
    try {
      const configData: TradeImportConfig = {
        name: name.trim(),
        module,
        importContext,
        lookupField: importContext === 'Expiry' ? lookupField : undefined,
        sourceType,
        enabled,
        startRow: startRow > 1 ? startRow : undefined,
        startColumn: sourceType === 'EXCEL' && startColumn.trim() ? startColumn.trim().toUpperCase() : undefined,
        includesHeaders: includesHeaders === false ? false : undefined,
        detectionHints: [],
        columnMappings,
        valueTransforms,
        conditionalFields: conditionalFields.length > 0 ? conditionalFields : undefined,
        computedFields: computedFields.length > 0 ? computedFields : undefined,
        skipRules: skipRules.length > 0 ? skipRules : undefined,
        defaultValues: defaultValues
          .filter((dv) => dv.field && dv.value.trim())
          .reduce((acc, dv) => ({ ...acc, [dv.field]: dv.value.trim() }), {} as Record<string, string>),
        deferredFields: importContext === 'Subscription' && deferredFields.length > 0 ? deferredFields : undefined,
        sampleHeaders: sampleHeaders.length > 0 ? sampleHeaders : undefined,
      };

      if (mode === 'edit' && editingId) {
        await tradeImportService.updateConfig(clientId, module, editingId, configData);
        showSuccess(t('commodities.tradeImport.saveSuccess'));
      } else {
        await tradeImportService.createConfig(clientId, module, configData);
        showSuccess(t('commodities.tradeImport.createSuccess'));
      }

      await loadConfigs();
      handleBackToList();
    } catch (err: any) {
      console.error('Failed to save config:', err);
      showError(mode === 'edit'
        ? t('commodities.tradeImport.saveError')
        : t('commodities.tradeImport.createError')
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Delete
  // ------------------------------------------------------------------

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm(t('commodities.tradeImport.deleteConfirm'))) return;

    setDeleting(true);
    try {
      await tradeImportService.deleteConfig(clientId, module, editingId);
      showSuccess(t('commodities.tradeImport.deleteSuccess'));
      await loadConfigs();
      handleBackToList();
    } catch (err: any) {
      console.error('Failed to delete config:', err);
      showError(t('commodities.tradeImport.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------------------------------------
  // Column Mappings CRUD
  // ------------------------------------------------------------------

  const removeMapping = (index: number) => {
    setColumnMappings(columnMappings.filter((_, i) => i !== index));
  };

  const updateMappingTarget = (index: number, targetField: string) => {
    const updated = [...columnMappings];
    updated[index] = { ...updated[index], targetFields: [targetField] };
    setColumnMappings(updated);
  };

  const duplicateMapping = (index: number) => {
    const source = columnMappings[index];
    setColumnMappings([
      ...columnMappings.slice(0, index + 1),
      { sourceColumn: source.sourceColumn, targetFields: [''] },
      ...columnMappings.slice(index + 1),
    ]);
  };

  // Update or create a transform entry for a given target field
  const updateRowTransform = (
    targetField: string,
    transformType: string | undefined,
    config: Record<string, any> | undefined,
  ) => {
    if (!transformType) {
      // Remove transform
      setValueTransforms(valueTransforms.filter((vt) => vt.targetField !== targetField));
      return;
    }
    const existingIdx = valueTransforms.findIndex((vt) => vt.targetField === targetField);
    if (existingIdx >= 0) {
      const updated = [...valueTransforms];
      updated[existingIdx] = { ...updated[existingIdx], transformType: transformType as any, config: config || {} };
      setValueTransforms(updated);
    } else {
      setValueTransforms([...valueTransforms, { targetField, transformType: transformType as any, config: config || {} }]);
    }
  };

  // ------------------------------------------------------------------
  // Transform config editor
  // ------------------------------------------------------------------

  const renderTransformConfig = (
    transformType: string,
    config: Record<string, any>,
    onChange: (config: Record<string, any>) => void,
    targetField: string,
  ) => {
    switch (transformType) {
      case 'DATE_FORMAT':
        return (
          <div className="ti-transform-config">
            <div className="ti-config-row">
              <label>{t('commodities.tradeImport.sourceFormat')}:</label>
              <select
                className="form-select form-select-sm"
                value={config.sourceFormat || ''}
                onChange={(e) => onChange({ ...config, sourceFormat: e.target.value })}
              >
                <option value="">{t('commodities.tradeImport.selectFormat')}</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                <option value="YYYYMMDD">YYYYMMDD</option>
                <option value="DDMMYYYY">DDMMYYYY</option>
              </select>
            </div>
          </div>
        );
      case 'ENUM_MAP': {
        const knownEnums = fieldEnums[targetField];
        const currentMappings: Record<string, string> = config.mappings || {};

        if (knownEnums) {
          // Dynamic rows: source value → FX+ value dropdown (supports N-to-1)
          const mappingEntries = Object.entries(currentMappings);

          return (
            <div className="ti-transform-config">
              <label>{t('commodities.tradeImport.enumMapLabel')}:</label>
              <div className="ti-enum-table">
                <div className="ti-enum-header">
                  <span>{t('commodities.tradeImport.sourceSystemValue')}</span>
                  <span></span>
                  <span>{t('commodities.tradeImport.fxPlusValue')}</span>
                  <span></span>
                </div>
                {mappingEntries.map(([src, tgt], idx) => (
                  <div key={idx} className="ti-enum-row">
                    <input
                      type="text"
                      className="form-input form-input-sm ti-config-input"
                      defaultValue={src}
                      onBlur={(e) => {
                        if (e.target.value === src) return;
                        const newMappings: Record<string, string> = {};
                        mappingEntries.forEach(([s, t], j) => {
                          newMappings[j === idx ? e.target.value : s] = t as string;
                        });
                        onChange({ ...config, mappings: newMappings });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                      placeholder={t('commodities.tradeImport.sourceValuePlaceholder')}
                    />
                    <span className="ti-arrow">&rarr;</span>
                    <select
                      className="form-select form-select-sm"
                      value={tgt as string || ''}
                      onChange={(e) => {
                        const newMappings: Record<string, string> = {};
                        mappingEntries.forEach(([s, t], j) => {
                          newMappings[s] = j === idx ? e.target.value : (t as string);
                        });
                        onChange({ ...config, mappings: newMappings });
                      }}
                    >
                      <option value="">--</option>
                      {knownEnums.map((ev) => (
                        <option key={ev.value} value={ev.value}>{ev.label}</option>
                      ))}
                    </select>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => {
                        const newMappings: Record<string, string> = {};
                        mappingEntries.forEach(([s, t], j) => {
                          if (j !== idx) newMappings[s] = t as string;
                        });
                        onChange({ ...config, mappings: newMappings });
                      }}
                      title={t('commodities.tradeImport.conditional.remove')}
                    >×</button>
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm ti-cf-add-btn"
                  onClick={() => {
                    onChange({ ...config, mappings: { ...currentMappings, '': '' } });
                  }}
                >+ {t('commodities.tradeImport.addEnumMapping')}</button>
              </div>
            </div>
          );
        }

        // Fallback for fields without known enums: key-value pair inputs
        const entries = Object.entries(currentMappings);
        return (
          <div className="ti-transform-config">
            <label>{t('commodities.tradeImport.enumMappings')}:</label>
            {entries.map(([src, tgt], idx) => (
              <div key={idx} className="ti-config-row">
                <input
                  type="text"
                  className="form-input form-input-sm ti-config-input"
                  defaultValue={src}
                  onBlur={(e) => {
                    if (e.target.value === src) return;
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[j === idx ? e.target.value : s] = t as string;
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  placeholder={t('commodities.tradeImport.sourceValue')}
                />
                <span className="ti-arrow">&rarr;</span>
                <input
                  type="text"
                  className="form-input form-input-sm ti-config-input"
                  value={tgt as string}
                  onChange={(e) => {
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[s] = j === idx ? e.target.value : (t as string);
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  placeholder={t('commodities.tradeImport.fxPlusValue')}
                />
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => {
                    const newMappings = { ...currentMappings };
                    delete newMappings[src];
                    onChange({ ...config, mappings: newMappings });
                  }}
                >×</button>
              </div>
            ))}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onChange({ ...config, mappings: { ...currentMappings, '': '' } })}
            >+ {t('commodities.tradeImport.addEnumMapping')}</button>
          </div>
        );
      }
      case 'NUMBER_FORMAT':
        return (
          <div className="ti-transform-config">
            <div className="ti-config-row">
              <label>{t('commodities.tradeImport.numberFormat')}:</label>
              <select
                className="form-select form-select-sm"
                value={config.numberFormat || ''}
                onChange={(e) => onChange({ ...config, numberFormat: e.target.value })}
              >
                <option value="">{t('commodities.tradeImport.selectFormat')}</option>
                <option value="1,234.56">1,234.56</option>
                <option value="1.234,56">1.234,56</option>
                <option value="1234.56">1234.56</option>
                <option value="1234,56">1234,56</option>
                <option value="1234">1234</option>
              </select>
            </div>
          </div>
        );
      case 'TEXT_REPLACE':
        return (
          <TextReplaceBuilder
            regexPattern={config.regexPattern || ''}
            regexReplacement={config.regexReplacement || ''}
            onChange={(pattern, replacement) => onChange({
              ...config,
              regexPattern: pattern,
              regexReplacement: replacement,
            })}
          />
        );
      case 'TEXT_TRANSFORM':
        return (
          <div className="ti-transform-config">
            <div className="ti-config-row">
              <label>{t('commodities.tradeImport.textTransform')}:</label>
              <select
                className="form-select form-select-sm"
                value={config.textTransform || 'UPPERCASE'}
                onChange={(e) => onChange({ ...config, textTransform: e.target.value })}
              >
                <option value="UPPERCASE">{t('commodities.tradeImport.uppercase')}</option>
                <option value="LOWERCASE">{t('commodities.tradeImport.lowercase')}</option>
              </select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ------------------------------------------------------------------
  // Default Values CRUD
  // ------------------------------------------------------------------

  const addDefaultValue = () => {
    setDefaultValues([...defaultValues, { field: '', value: '' }]);
  };

  const updateDefaultValue = (index: number, key: 'field' | 'value', val: string) => {
    const updated = [...defaultValues];
    updated[index] = { ...updated[index], [key]: val };
    setDefaultValues(updated);
    // Auto-remove from deferred fields when a default value is set
    const fieldName = key === 'field' ? val : updated[index].field;
    const fieldValue = key === 'value' ? val : updated[index].value;
    if (fieldName && fieldValue && deferredFields.includes(fieldName)) {
      setDeferredFields(deferredFields.filter((df) => df !== fieldName));
    }
  };

  const removeDefaultValue = (index: number) => {
    setDefaultValues(defaultValues.filter((_, i) => i !== index));
  };

  // ------------------------------------------------------------------
  // Conditional Fields CRUD
  // ------------------------------------------------------------------

  const addConditionalField = () => {
    const newEntry: ConditionalFieldEntry = {
      targetField: '',
      branches: [{
        conditionGroups: [{ conditions: [{ field: '', operator: 'EQUALS', value: '' }] }],
        output: { type: 'STATIC', value: '' },
      }],
      elseOutput: { type: 'STATIC', value: '' },
    };
    setConditionalFields([...conditionalFields, newEntry]);
    setExpandedCFIndex(conditionalFields.length); // auto-expand new entry
  };

  const removeConditionalField = (index: number) => {
    setConditionalFields(conditionalFields.filter((_, i) => i !== index));
  };

  const updateConditionalField = (index: number, patch: Partial<ConditionalFieldEntry>) => {
    setConditionalFields(conditionalFields.map((cf, i) =>
      i === index ? { ...cf, ...patch } : cf
    ));
  };

  const updateBranch = (cfIdx: number, branchIdx: number, patch: Partial<ConditionalFieldBranch>) => {
    const entry = conditionalFields[cfIdx];
    const newBranches = entry.branches.map((b, i) =>
      i === branchIdx ? { ...b, ...patch } : b
    );
    updateConditionalField(cfIdx, { branches: newBranches });
  };

  const addElseIfBranch = (cfIdx: number) => {
    const entry = conditionalFields[cfIdx];
    updateConditionalField(cfIdx, {
      branches: [...entry.branches, {
        conditionGroups: [{ conditions: [{ field: '', operator: 'EQUALS', value: '' }] }],
        output: { type: 'STATIC', value: '' },
      }],
    });
  };

  const removeElseIfBranch = (cfIdx: number, branchIdx: number) => {
    const entry = conditionalFields[cfIdx];
    if (entry.branches.length <= 1) return; // must keep at least one IF branch
    updateConditionalField(cfIdx, {
      branches: entry.branches.filter((_, i) => i !== branchIdx),
    });
  };

  const updateCFCondition = (cfIdx: number, branchIdx: number, gi: number, ci: number, patch: Record<string, any>) => {
    const entry = conditionalFields[cfIdx];
    const branch = entry.branches[branchIdx];
    const newGroups = branch.conditionGroups.map((g, gIdx) =>
      gIdx === gi
        ? { ...g, conditions: g.conditions.map((c, cIdx) => (cIdx === ci ? { ...c, ...patch } : c)) }
        : g
    );
    updateBranch(cfIdx, branchIdx, { conditionGroups: newGroups });
  };

  const addCFCondition = (cfIdx: number, branchIdx: number, gi: number) => {
    const entry = conditionalFields[cfIdx];
    const branch = entry.branches[branchIdx];
    const newGroups = branch.conditionGroups.map((g, gIdx) =>
      gIdx === gi ? { ...g, conditions: [...g.conditions, { field: '', operator: 'EQUALS', value: '' }] } : g
    );
    updateBranch(cfIdx, branchIdx, { conditionGroups: newGroups });
  };

  const removeCFCondition = (cfIdx: number, branchIdx: number, gi: number, ci: number) => {
    const entry = conditionalFields[cfIdx];
    const branch = entry.branches[branchIdx];
    const group = branch.conditionGroups[gi];
    if (group.conditions.length <= 1) {
      if (branch.conditionGroups.length <= 1) return;
      updateBranch(cfIdx, branchIdx, {
        conditionGroups: branch.conditionGroups.filter((_, i) => i !== gi),
      });
    } else {
      const newGroups = branch.conditionGroups.map((g, gIdx) =>
        gIdx === gi ? { ...g, conditions: g.conditions.filter((_, cIdx) => cIdx !== ci) } : g
      );
      updateBranch(cfIdx, branchIdx, { conditionGroups: newGroups });
    }
  };

  const addCFOrGroup = (cfIdx: number, branchIdx: number) => {
    const entry = conditionalFields[cfIdx];
    const branch = entry.branches[branchIdx];
    updateBranch(cfIdx, branchIdx, {
      conditionGroups: [...branch.conditionGroups, { conditions: [{ field: '', operator: 'EQUALS', value: '' }] }],
    });
  };

  // ------------------------------------------------------------------
  // Skip Rules CRUD
  // ------------------------------------------------------------------

  const addSkipRule = () => {
    setSkipRules([...skipRules, { conditions: [{ field: '', operator: 'IS_NULL', value: '' }] }]);
  };

  const removeSkipRule = (groupIdx: number) => {
    setSkipRules(skipRules.filter((_, i) => i !== groupIdx));
  };

  const updateSkipCondition = (
    groupIdx: number,
    condIdx: number,
    updates: Partial<ConditionalFieldCondition>,
  ) => {
    const updated = [...skipRules];
    const group = { ...updated[groupIdx], conditions: [...updated[groupIdx].conditions] };
    group.conditions[condIdx] = { ...group.conditions[condIdx], ...updates };
    updated[groupIdx] = group;
    setSkipRules(updated);
  };

  const addSkipCondition = (groupIdx: number) => {
    const updated = [...skipRules];
    const group = { ...updated[groupIdx], conditions: [...updated[groupIdx].conditions] };
    group.conditions.push({ field: '', operator: 'IS_NULL', value: '' });
    updated[groupIdx] = group;
    setSkipRules(updated);
  };

  const removeSkipCondition = (groupIdx: number, condIdx: number) => {
    const updated = [...skipRules];
    const group = { ...updated[groupIdx], conditions: [...updated[groupIdx].conditions] };
    if (group.conditions.length <= 1) return;
    group.conditions.splice(condIdx, 1);
    updated[groupIdx] = group;
    setSkipRules(updated);
  };

  // ------------------------------------------------------------------
  // Computed Fields CRUD (KAN-300)
  // ------------------------------------------------------------------

  const addComputedField = () => {
    const newEntry: ComputedFieldEntry = {
      targetField: '',
      parts: [{ source: '', extract: 'VALUE' as ExtractType }],
    };
    setComputedFields([...computedFields, newEntry]);
    setExpandedCompIndex(computedFields.length);
  };

  const removeComputedField = (index: number) => {
    setComputedFields(computedFields.filter((_, i) => i !== index));
    if (expandedCompIndex === index) setExpandedCompIndex(null);
  };

  const updateComputedField = (index: number, patch: Partial<ComputedFieldEntry>) => {
    setComputedFields(computedFields.map((cf, i) =>
      i === index ? { ...cf, ...patch } : cf
    ));
  };

  const addComputedPart = (cfIdx: number) => {
    const entry = computedFields[cfIdx];
    updateComputedField(cfIdx, {
      parts: [...entry.parts, { source: '', extract: 'VALUE' as ExtractType }],
    });
  };

  const removeComputedPart = (cfIdx: number, partIdx: number) => {
    const entry = computedFields[cfIdx];
    if (entry.parts.length <= 1) return;
    updateComputedField(cfIdx, {
      parts: entry.parts.filter((_, i) => i !== partIdx),
    });
  };

  const updateComputedPart = (cfIdx: number, partIdx: number, patch: Partial<ComputedFieldPart>) => {
    const entry = computedFields[cfIdx];
    updateComputedField(cfIdx, {
      parts: entry.parts.map((p, i) => i === partIdx ? { ...p, ...patch } : p),
    });
  };

  // ------------------------------------------------------------------
  // Helper: get field label
  // ------------------------------------------------------------------

  const getFieldLabel = (fieldName: string): string => {
    const field = targetFields.find((f) => f.value === fieldName);
    return field?.label || fieldName;
  };

  // ------------------------------------------------------------------
  // Render — Loading
  // ------------------------------------------------------------------

  if (mode === 'loading') {
    return (
      <div className="ti-config-panel">
        <div className="ti-loading">{t('common.loading')}</div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Render — Config List / Create / Edit Form
  // ------------------------------------------------------------------

  return (
    <div className="ti-config-panel">

    {/* ===== List View ===== */}
    {mode === 'list' && (
      <>
        <div className="ti-header">
          <h3 className="ti-title">{t(importContext === 'Expiry' ? 'commodities.tradeImport.expiryTitle' : 'commodities.tradeImport.title')}</h3>
          <button className="btn btn-primary" onClick={handleCreate}>
            + {t('commodities.tradeImport.createConfig')}
          </button>
        </div>

        {configs.length > 0 && (
          <div className="ti-config-list">
            {configs.map((config) => (
              <div
                key={config.id}
                className="ti-config-card"
                onClick={() => handleEdit(config)}
              >
                <div className="ti-config-card-header">
                  <span className="ti-config-name">{config.name}</span>
                  <span className={`ti-config-badge ${config.enabled ? 'enabled' : 'disabled'}`}>
                    {config.enabled ? t('commodities.tradeImport.enabled') : 'Disabled'}
                  </span>
                </div>
                <div className="ti-config-card-meta">
                  <span className="ti-config-source-type">{config.sourceType}</span>
                  <span className="ti-config-mappings">
                    {config.columnMappings?.length || 0} mappings
                  </span>
                </div>
                {config.updatedAt && (
                  <div className="ti-config-card-footer">
                    {t('commodities.tradeImport.lastUpdated')}: {(() => { const d = new Date(config.updatedAt); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    )}

    {/* ===== Create / Edit Form ===== */}
    {(mode === 'create' || mode === 'edit') && (
      <>
      <div className="ti-header">
        <h3 className="ti-title">
          {mode === 'edit'
            ? t('commodities.tradeImport.editConfig')
            : t('commodities.tradeImport.createConfig')}
        </h3>
        <button className="btn btn-secondary" onClick={handleBackToList}>
          {t('commodities.tradeImport.backToList')}
        </button>
      </div>

      {/* Basic Info */}
      <div className="ti-section">
        <div className="ti-form-row">
          <label className="form-label">{t('commodities.tradeImport.configName')}</label>
          <input
            type="text"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
          />
        </div>

        {importContext === 'Expiry' && (
          <div className="ti-form-row">
            <label className="form-label">{t('commodities.expiryImport.lookupField')}</label>
            <select
              className="form-select"
              value={lookupField}
              onChange={(e) => setLookupField(e.target.value)}
            >
              {LOOKUP_FIELD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
            <p className="ti-section-description" style={{ marginTop: '0.25rem' }}>
              {t('commodities.expiryImport.lookupFieldHelper')}
            </p>
          </div>
        )}

        <div className="ti-form-row-group">
          <div className="ti-form-row">
            <label className="form-label">{t('commodities.tradeImport.sourceType')}</label>
            <select
              className="form-select"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as 'CSV' | 'EXCEL')}
            >
              {SOURCE_TYPES.map((st) => (
                <option key={st.value} value={st.value}>{st.label}</option>
              ))}
            </select>
          </div>

          <div className="ti-form-row">
            <label className="form-label">{t('commodities.tradeImport.startRow')}</label>
            <input
              type="number"
              className="form-input"
              value={startRow}
              min={1}
              onChange={(e) => setStartRow(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ maxWidth: 100 }}
            />
          </div>

          {sourceType === 'EXCEL' && (
            <div className="ti-form-row">
              <label className="form-label">{t('commodities.tradeImport.startColumn')}</label>
              <input
                type="text"
                className="form-input"
                value={startColumn}
                onChange={(e) => setStartColumn(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                placeholder="A"
                maxLength={3}
                style={{ maxWidth: 80 }}
              />
            </div>
          )}

          <div className="ti-form-row ti-form-row-checkbox">
            <label className="ti-checkbox-label">
              <input
                type="checkbox"
                checked={includesHeaders}
                onChange={(e) => setIncludesHeaders(e.target.checked)}
              />
              {t('commodities.tradeImport.includesHeaders')}
            </label>
          </div>

          <div className="ti-form-row ti-form-row-checkbox">
            <label className="ti-checkbox-label">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              {t('commodities.tradeImport.enabled')}
            </label>
          </div>
        </div>

        {/* Sample File Upload */}
        <div className="ti-sample-section">
          <label className="form-label">{t('commodities.tradeImport.sampleFileLabel')}</label>
          <input
            ref={sampleFileRef}
            type="file"
            accept={sourceType === 'CSV' ? '.csv' : sourceType === 'EXCEL' ? '.xlsx,.xls' : '.csv,.xlsx,.xls'}
            onChange={handleSampleFile}
            style={{ display: 'none' }}
          />
          {sampleHeaders.length === 0 ? (
            <div
              className="ti-sample-dropzone"
              onClick={() => sampleFileRef.current?.click()}
            >
              <span className="ti-sample-dropzone-text">
                {t('commodities.tradeImport.sampleDropzoneText')}
              </span>
            </div>
          ) : (
            <div className="ti-sample-loaded">
              <div className="ti-sample-loaded-info">
                <span className="ti-sample-filename">{sampleFileName}</span>
                <span className="ti-sample-count">
                  {t('commodities.tradeImport.sampleHeaderCount', { count: sampleHeaders.length })}
                </span>
              </div>
              <div className="ti-sample-loaded-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => sampleFileRef.current?.click()}
                >
                  {t('commodities.tradeImport.changeSample')}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={clearSampleHeaders}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unified Field Mapping Table — one row per source column */}
      {(sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.fieldMapping')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.fieldMappingDescription')}</p>

        <table className="ti-table">
          <thead>
            <tr>
              <th>{t('commodities.tradeImport.sourceColumn')}</th>
              <th>{t('commodities.tradeImport.targetField')}</th>
              <th>{t('commodities.tradeImport.transformType')}</th>
              <th>{t('commodities.tradeImport.action')}</th>
            </tr>
          </thead>
          <tbody>
            {columnMappings.map((mapping, index) => {
              const targetField = mapping.targetFields[0] || '';
              const transform = valueTransforms.find((vt) => vt.targetField === targetField);
              return (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <span className="ti-field-label">{mapping.sourceColumn}</span>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={targetField}
                        onChange={(e) => updateMappingTarget(index, e.target.value)}
                      >
                        <option value="">--</option>
                        {mappableTargetFields.map((f) => (
                          <option key={f.value} value={f.value} disabled={f.calculated}>{f.label}{f.calculated ? ` (${t('commodities.tradeImport.calculated')})` : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {targetField ? (
                        transform?.transformType ? (
                          <span
                            className="ti-transform-pill"
                            onClick={() => setExpandedRowIndex(expandedRowIndex === index ? null : index)}
                          >
                            {t(`commodities.tradeImport.transformTypes.${transform.transformType}`)}
                          </span>
                        ) : (
                          <button
                            className="btn-icon ti-add-transform-btn"
                            title={t('commodities.tradeImport.addTransform')}
                            onClick={() => {
                              updateRowTransform(targetField, 'DATE_FORMAT', {});
                              setExpandedRowIndex(index);
                            }}
                          >
                            +
                          </button>
                        )
                      ) : (
                        <span className="ti-field-label">--</span>
                      )}
                    </td>
                    <td>
                      <span className="ti-cell-actions">
                        <button
                          className="btn-icon"
                          title={t('commodities.tradeImport.duplicateRow')}
                          onClick={() => duplicateMapping(index)}
                        >
                          ⧉
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          title={t('commodities.tradeImport.removeMapping')}
                          onClick={() => {
                            removeMapping(index);
                            if (targetField) {
                              setValueTransforms(valueTransforms.filter((vt) => vt.targetField !== targetField));
                            }
                            if (expandedRowIndex === index) setExpandedRowIndex(null);
                          }}
                        >
                          ×
                        </button>
                      </span>
                    </td>
                  </tr>
                  {/* Expanded transform config panel */}
                  {expandedRowIndex === index && targetField && transform?.transformType && (
                    <tr className="ti-expand-row">
                      <td colSpan={4}>
                        <div className="ti-row-expand">
                          <div className="ti-row-expand-header">
                            <select
                              className="form-select form-select-sm"
                              value={transform.transformType}
                              onChange={(e) => updateRowTransform(targetField, e.target.value, {})}
                            >
                              {TRANSFORM_TYPE_KEYS.map((key) => (
                                <option key={key} value={key}>{t(`commodities.tradeImport.transformTypes.${key}`)}</option>
                              ))}
                            </select>
                            <button
                              className="btn-icon"
                              title={t('common.close')}
                              onClick={() => setExpandedRowIndex(null)}
                            >
                              ▲
                            </button>
                          </div>
                          {renderTransformConfig(
                            transform.transformType,
                            transform.config || {},
                            (newConfig) => updateRowTransform(targetField, transform.transformType, newConfig),
                            targetField,
                          )}
                          <div className="ti-row-expand-footer">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setExpandedRowIndex(null)}
                            >
                              {t('common.ok')}
                            </button>
                            <button
                              className="btn btn-secondary btn-sm ti-remove-transform-btn"
                              onClick={() => {
                                updateRowTransform(targetField, undefined, undefined);
                                setExpandedRowIndex(null);
                              }}
                            >
                              {t('commodities.tradeImport.removeTransform')}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {/* Show unmapped sample headers as available rows */}
            {sampleHeaders
              .filter((h) => !columnMappings.some((m) => m.sourceColumn === h))
              .map((header) => (
                <tr key={`unmapped-${header}`} className="ti-unmapped-row">
                  <td>
                    <span className="ti-field-label">{header}</span>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setColumnMappings([...columnMappings, { sourceColumn: header, targetFields: [e.target.value] }]);
                        }
                      }}
                    >
                      <option value="">{t('commodities.tradeImport.selectTargetField')}</option>
                      {mappableTargetFields.map((f) => (
                        <option key={f.value} value={f.value} disabled={f.calculated}>{f.label}{f.calculated ? ` (${t('commodities.tradeImport.calculated')})` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td><span className="ti-field-label">--</span></td>
                  <td></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Conditional Fields — set target fields based on conditions */}
      {(sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.conditionalFields')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.conditionalFieldsDescription')}</p>

        {conditionalFields.map((cf, cfIdx) => {
          const isExpanded = expandedCFIndex === cfIdx;
          const targetLabel = cf.targetField ? getFieldLabel(cf.targetField) : t('commodities.tradeImport.conditional.selectTargetField');
          const condCount = cf.branches.reduce((sum: number, b: ConditionalFieldBranch) => b.conditionGroups.reduce((s: number, g: { conditions: ConditionalFieldCondition[] }) => s + g.conditions.length, sum), 0);

          /** Render an output value row (reused for branch outputs and elseOutput) */
          const renderOutputRow = (
            output: ConditionalFieldOutput,
            onUpdate: (newOutput: ConditionalFieldOutput) => void,
          ) => {
            const transformType = output.transform?.transformType || '';
            const transformConfig = output.transform?.config || {};
            return (
            <div className="ti-cf-output-row">
              <select
                className="form-select form-select-sm"
                value={output.type || 'STATIC'}
                onChange={(e) => onUpdate({ type: e.target.value as 'STATIC' | 'FIELD', value: '' })}
              >
                {CONDITIONAL_OUTPUT_TYPES.map((ot) => (
                  <option key={ot.value} value={ot.value}>{t(ot.labelKey)}</option>
                ))}
              </select>
              {output.type === 'FIELD' ? (
                <select
                  className="form-select form-select-sm"
                  value={output.value || ''}
                  onChange={(e) => onUpdate({ ...output, value: e.target.value })}
                >
                  <option value="">{t('commodities.tradeImport.conditional.selectField')}</option>
                  {sampleHeaders.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              ) : cf.targetField && fieldEnums[cf.targetField] ? (
                <select
                  className="form-select form-select-sm"
                  value={output.value || ''}
                  onChange={(e) => onUpdate({ ...output, value: e.target.value })}
                >
                  <option value="">{t('commodities.tradeImport.conditional.selectValue')}</option>
                  {fieldEnums[cf.targetField].map((ev) => (
                    <option key={ev.value} value={ev.value}>{ev.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-input form-input-sm ti-config-input"
                  value={output.value || ''}
                  onChange={(e) => onUpdate({ ...output, value: e.target.value })}
                  placeholder={t('commodities.tradeImport.conditional.outputValue')}
                />
              )}
              {output.type === 'FIELD' && (
                <>
                  <select
                    className="form-select form-select-sm ti-cf-transform-select"
                    value={transformType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      onUpdate({ ...output, transform: newType ? { transformType: newType, config: {} } : undefined });
                    }}
                  >
                    <option value="">{t('commodities.tradeImport.conditional.noTransform')}</option>
                    {TRANSFORM_TYPE_KEYS.map((key) => (
                      <option key={key} value={key}>{t(`commodities.tradeImport.transformTypes.${key}`)}</option>
                    ))}
                  </select>
                  {transformType === 'DATE_FORMAT' && (
                    <select
                      className="form-select form-select-sm ti-cf-transform-select"
                      value={transformConfig.sourceFormat || ''}
                      onChange={(e) => onUpdate({ ...output, transform: { transformType: 'DATE_FORMAT', config: { sourceFormat: e.target.value } } })}
                    >
                      <option value="">{t('commodities.tradeImport.selectFormat')}</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                      <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYYMMDD">YYYYMMDD</option>
                      <option value="DDMMYYYY">DDMMYYYY</option>
                    </select>
                  )}
                  {transformType === 'NUMBER_FORMAT' && (
                    <>
                      <select
                        className="form-select form-select-sm ti-cf-transform-select"
                        value={transformConfig.thousandSeparator || ''}
                        onChange={(e) => onUpdate({ ...output, transform: { transformType: 'NUMBER_FORMAT', config: { ...transformConfig, thousandSeparator: e.target.value } } })}
                      >
                        <option value="">{t('commodities.tradeImport.thousandSeparator')}</option>
                        <option value=",">,</option>
                        <option value=".">.</option>
                        <option value=" "> (space)</option>
                      </select>
                      <select
                        className="form-select form-select-sm ti-cf-transform-select"
                        value={transformConfig.decimalSeparator || ''}
                        onChange={(e) => onUpdate({ ...output, transform: { transformType: 'NUMBER_FORMAT', config: { ...transformConfig, decimalSeparator: e.target.value } } })}
                      >
                        <option value="">{t('commodities.tradeImport.decimalSeparator')}</option>
                        <option value=".">.</option>
                        <option value=",">,</option>
                      </select>
                    </>
                  )}
                  {transformType === 'TEXT_TRANSFORM' && (
                    <select
                      className="form-select form-select-sm ti-cf-transform-select"
                      value={transformConfig.transform || ''}
                      onChange={(e) => onUpdate({ ...output, transform: { transformType: 'TEXT_TRANSFORM', config: { transform: e.target.value } } })}
                    >
                      <option value="">{t('commodities.tradeImport.textTransform')}</option>
                      <option value="UPPERCASE">{t('commodities.tradeImport.uppercase')}</option>
                      <option value="LOWERCASE">{t('commodities.tradeImport.lowercase')}</option>
                    </select>
                  )}
                </>
              )}
            </div>
            );
          };

          return (
          <div key={cfIdx} className={`ti-cf-entry ${isExpanded ? 'ti-cf-entry-expanded' : 'ti-cf-entry-collapsed'}`}>
            <div
              className="ti-cf-entry-header"
              onClick={() => setExpandedCFIndex(isExpanded ? null : cfIdx)}
            >
              <div className="ti-cf-header-left">
                <span className="ti-cf-badge">IF</span>
                {!isExpanded && (
                  <span className="ti-cf-summary">
                    {condCount} {condCount === 1 ? t('commodities.tradeImport.conditional.operator') : t('commodities.tradeImport.conditional.conditions')}
                    {cf.branches.length > 1 ? ` (${cf.branches.length} ${t('commodities.tradeImport.conditional.branches')})` : ''}
                    {' → '}
                    <strong>{targetLabel}</strong>
                  </span>
                )}
              </div>
              <div className="ti-cf-header-right">
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={(e) => { e.stopPropagation(); removeConditionalField(cfIdx); }}
                  title={t('commodities.tradeImport.conditional.remove')}
                >×</button>
                <span className="ti-cf-chevron">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
            <>
            {/* Target field (moved above branches for clarity) */}
            <div className="ti-cf-target-row">
              <label>{t('commodities.tradeImport.conditional.setField')}:</label>
              <select
                className="form-select form-select-sm"
                value={cf.targetField}
                onChange={(e) => {
                  const newBranches = cf.branches.map((b) => ({ ...b, output: { ...b.output, value: '' } }));
                  updateConditionalField(cfIdx, {
                    targetField: e.target.value,
                    branches: newBranches,
                    elseOutput: { ...cf.elseOutput, value: '' },
                  });
                }}
              >
                <option value="">{t('commodities.tradeImport.conditional.selectTargetField')}</option>
                {targetFields.map((f) => (
                  <option key={f.value} value={f.value} disabled={f.calculated}>{f.label}{f.calculated ? ` (${t('commodities.tradeImport.calculated')})` : ''}</option>
                ))}
              </select>
            </div>

            {/* IF / ELSE IF branches */}
            {cf.branches.map((branch, bi) => (
              <div key={bi} className="ti-cf-branch">
                <div className="ti-cf-branch-header">
                  <span className="ti-cf-badge">{bi === 0 ? 'IF' : 'ELSE IF'}</span>
                  {cf.branches.length > 1 && (
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => removeElseIfBranch(cfIdx, bi)}
                      title={t('commodities.tradeImport.conditional.remove')}
                    >×</button>
                  )}
                </div>

                {/* Condition groups for this branch */}
                {branch.conditionGroups.map((group, gi) => (
                  <React.Fragment key={gi}>
                    {gi > 0 && <div className="ti-cf-or-separator">{t('commodities.tradeImport.conditional.or')}</div>}
                    <div className="ti-cf-group">
                      {group.conditions.map((cond, ci) => {
                        const needsValue = cond.operator !== 'IS_NULL' && cond.operator !== 'IS_NOT_NULL';
                        return (
                          <React.Fragment key={ci}>
                            {ci > 0 && <div className="ti-cf-and-label">{t('commodities.tradeImport.conditional.and')}</div>}
                            <div className="ti-cf-condition-row">
                              <select
                                className="form-select form-select-sm"
                                value={cond.field}
                                onChange={(e) => updateCFCondition(cfIdx, bi, gi, ci, { field: e.target.value })}
                              >
                                <option value="">{t('commodities.tradeImport.conditional.selectField')}</option>
                                {sampleHeaders.map((h) => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <select
                                className="form-select form-select-sm"
                                value={cond.operator}
                                onChange={(e) => updateCFCondition(cfIdx, bi, gi, ci, { operator: e.target.value })}
                              >
                                {CONDITIONAL_OPERATORS.map((op) => (
                                  <option key={op.value} value={op.value}>{t(op.labelKey)}</option>
                                ))}
                              </select>
                              {needsValue && (
                                <input
                                  type="text"
                                  className="form-input form-input-sm ti-config-input"
                                  value={cond.value || ''}
                                  onChange={(e) => updateCFCondition(cfIdx, bi, gi, ci, { value: e.target.value })}
                                  placeholder={t('commodities.tradeImport.conditional.conditionValue')}
                                />
                              )}
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => removeCFCondition(cfIdx, bi, gi, ci)}
                                title={t('commodities.tradeImport.conditional.remove')}
                              >×</button>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <button
                        className="btn btn-secondary btn-sm ti-cf-add-btn"
                        onClick={() => addCFCondition(cfIdx, bi, gi)}
                      >+ {t('commodities.tradeImport.conditional.addAnd')}</button>
                    </div>
                  </React.Fragment>
                ))}
                <button
                  className="btn btn-secondary btn-sm ti-cf-add-btn"
                  onClick={() => addCFOrGroup(cfIdx, bi)}
                >+ {t('commodities.tradeImport.conditional.addOr')}</button>

                {/* THEN output for this branch */}
                <div className="ti-cf-output">
                  <label>{t('commodities.tradeImport.conditional.thenOutput')}:</label>
                  {renderOutputRow(branch.output, (newOutput) => updateBranch(cfIdx, bi, { output: newOutput }))}
                </div>
              </div>
            ))}

            {/* Add ELSE IF button */}
            <button
              className="btn btn-secondary btn-sm ti-cf-add-btn"
              onClick={() => addElseIfBranch(cfIdx)}
            >+ {t('commodities.tradeImport.conditional.addElseIf')}</button>

            {/* ELSE output */}
            <div className="ti-cf-output">
              <label><span className="ti-cf-badge">{t('commodities.tradeImport.conditional.elseOutput')}</span>:</label>
              {renderOutputRow(cf.elseOutput, (newOutput) => updateConditionalField(cfIdx, { elseOutput: newOutput }))}
            </div>

            {/* OK button to collapse */}
            <div className="ti-cf-footer">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setExpandedCFIndex(null)}
              >
                {t('common.ok')}
              </button>
            </div>
            </>
            )}
          </div>
          );
        })}

        <button
          className="btn btn-secondary btn-sm"
          onClick={addConditionalField}
        >
          + {t('commodities.tradeImport.addConditionalField')}
        </button>
      </div>
      )}

      {/* Computed Fields — derive new fields by combining parts from multiple sources */}
      {(sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.computed.title')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.computed.description')}</p>

        {computedFields.map((cf, cfIdx) => {
          const isExpanded = expandedCompIndex === cfIdx;
          const targetLabel = cf.targetField ? getFieldLabel(cf.targetField) : t('commodities.tradeImport.conditional.selectTargetField');

          return (
          <div key={cfIdx} className={`ti-comp-entry ${isExpanded ? 'ti-comp-entry-expanded' : 'ti-comp-entry-collapsed'}`}>
            <div
              className="ti-comp-entry-header"
              onClick={() => setExpandedCompIndex(isExpanded ? null : cfIdx)}
            >
              <div className="ti-comp-header-left">
                <span className="ti-comp-badge">COMPUTE</span>
                {!isExpanded && (
                  <span className="ti-comp-summary">
                    {cf.parts.length} {t('commodities.tradeImport.computed.partCount', { count: cf.parts.length })}
                    {' → '}
                    <strong>{targetLabel}</strong>
                  </span>
                )}
              </div>
              <div className="ti-comp-header-right">
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={(e) => { e.stopPropagation(); removeComputedField(cfIdx); }}
                  title={t('commodities.tradeImport.conditional.remove')}
                >×</button>
                <span className="ti-cf-chevron">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
            <>
            {/* Target field */}
            <div className="ti-comp-target-row">
              <label>{t('commodities.tradeImport.computed.targetField')}:</label>
              <select
                className="form-select form-select-sm"
                value={cf.targetField}
                onChange={(e) => updateComputedField(cfIdx, { targetField: e.target.value })}
              >
                <option value="">{t('commodities.tradeImport.conditional.selectTargetField')}</option>
                {targetFields.map((f) => (
                  <option key={f.value} value={f.value} disabled={f.calculated}>{f.label}{f.calculated ? ` (${t('commodities.tradeImport.calculated')})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Parts */}
            <div className="ti-comp-parts">
              {cf.parts.map((part, pi) => {
                const showOffset = DATE_EXTRACT_TYPES.has(part.extract);
                const showLength = LENGTH_EXTRACT_TYPES.has(part.extract);
                const partTransformType = part.transform?.transformType || '';
                const partTransformConfig = part.transform?.config || {};

                return (
                <div key={pi} className="ti-comp-part-row">
                  {/* Source field — mapped target fields + raw source columns */}
                  <select
                    className="form-select form-select-sm"
                    value={part.source}
                    onChange={(e) => updateComputedPart(cfIdx, pi, { source: e.target.value })}
                  >
                    <option value="">{t('commodities.tradeImport.computed.sourceField')}</option>
                    {(() => {
                      const mappedFieldNames = new Set([
                        ...columnMappings.flatMap((m) => m.targetFields).filter(Boolean),
                        ...conditionalFields.map((cf) => cf.targetField).filter(Boolean),
                      ]);
                      const mappedOptions = targetFields.filter(
                        (f) => !f.calculated && mappedFieldNames.has(f.value)
                      );
                      const headers = sampleHeaders.length > 0
                        ? sampleHeaders
                        : (editingConfig?.sampleHeaders || []);
                      return (
                        <>
                          {mappedOptions.length > 0 && (
                            <optgroup label={t('commodities.tradeImport.computed.sourceGroupMapped')}>
                              {mappedOptions.map((f) => (
                                <option key={`mapped-${f.value}`} value={f.value}>{f.label}</option>
                              ))}
                            </optgroup>
                          )}
                          {headers.length > 0 && (
                            <optgroup label={t('commodities.tradeImport.computed.sourceGroupRaw')}>
                              {headers.map((h) => (
                                <option key={`raw-${h}`} value={h}>{h}</option>
                              ))}
                            </optgroup>
                          )}
                        </>
                      );
                    })()}
                  </select>

                  {/* Extract type */}
                  <select
                    className="form-select form-select-sm"
                    value={part.extract}
                    onChange={(e) => {
                      const newExtract = e.target.value as ExtractType;
                      const patch: Partial<ComputedFieldPart> = { extract: newExtract };
                      // Clear inapplicable fields
                      if (!DATE_EXTRACT_TYPES.has(newExtract)) patch.offset = undefined;
                      if (!LENGTH_EXTRACT_TYPES.has(newExtract)) patch.length = undefined;
                      updateComputedPart(cfIdx, pi, patch);
                    }}
                  >
                    {EXTRACT_TYPES.map((et) => (
                      <option key={et.value} value={et.value}>{t(et.labelKey)}</option>
                    ))}
                  </select>

                  {/* Offset (for date extracts) */}
                  {showOffset && (
                    <input
                      type="number"
                      className="form-input form-input-sm"
                      value={part.offset ?? 0}
                      onChange={(e) => updateComputedPart(cfIdx, pi, { offset: parseInt(e.target.value) || 0 })}
                      title={t('commodities.tradeImport.computed.offsetHelp')}
                      placeholder={t('commodities.tradeImport.computed.offset')}
                      style={{ maxWidth: 70 }}
                    />
                  )}

                  {/* Length (for FIRST/LAST) */}
                  {showLength && (
                    <input
                      type="number"
                      className="form-input form-input-sm"
                      value={part.length ?? 1}
                      min={1}
                      onChange={(e) => updateComputedPart(cfIdx, pi, { length: Math.max(1, parseInt(e.target.value) || 1) })}
                      placeholder={t('commodities.tradeImport.computed.length')}
                      style={{ maxWidth: 60 }}
                    />
                  )}

                  {/* Transform select */}
                  <select
                    className="form-select form-select-sm"
                    value={partTransformType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      updateComputedPart(cfIdx, pi, {
                        transform: newType ? { transformType: newType, config: {} } : undefined,
                      });
                    }}
                  >
                    <option value="">{t('commodities.tradeImport.computed.noTransform')}</option>
                    {TRANSFORM_TYPE_KEYS.map((key) => (
                      <option key={key} value={key}>{t(`commodities.tradeImport.transformTypes.${key}`)}</option>
                    ))}
                  </select>

                  {/* Remove part */}
                  {cf.parts.length > 1 && (
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => removeComputedPart(cfIdx, pi)}
                      title={t('commodities.tradeImport.conditional.remove')}
                    >×</button>
                  )}

                  {/* Inline transform config (ENUM_MAP is the most common) */}
                  {partTransformType && (
                    <div className="ti-comp-part-transform-config">
                      {renderTransformConfig(
                        partTransformType,
                        partTransformConfig,
                        (newConfig) => updateComputedPart(cfIdx, pi, {
                          transform: { transformType: partTransformType, config: newConfig },
                        }),
                        cf.targetField || '__computed__',
                      )}
                    </div>
                  )}
                </div>
                );
              })}

              <button
                className="btn btn-secondary btn-sm ti-cf-add-btn"
                onClick={() => addComputedPart(cfIdx)}
              >+ {t('commodities.tradeImport.computed.addPart')}</button>
            </div>

            {/* Separator */}
            <div className="ti-comp-separator-row">
              <label>{t('commodities.tradeImport.computed.separator')}:</label>
              <input
                type="text"
                className="form-input form-input-sm ti-comp-separator-input"
                value={cf.separator || ''}
                onChange={(e) => updateComputedField(cfIdx, { separator: e.target.value })}
                placeholder=""
                title={t('commodities.tradeImport.computed.separatorHelp')}
              />
            </div>

            {/* OK to collapse */}
            <div className="ti-comp-footer">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setExpandedCompIndex(null)}
              >
                {t('common.ok')}
              </button>
            </div>
            </>
            )}
          </div>
          );
        })}

        <button
          className="btn btn-secondary btn-sm"
          onClick={addComputedField}
        >
          + {t('commodities.tradeImport.computed.addComputedField')}
        </button>
      </div>
      )}

      {/* Skip Rules — filter out rows that match conditions */}
      {(sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.skipRules')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.skipRulesDescription')}</p>

        {skipRules.map((group, gi) => (
          <div key={gi} className="ti-skip-group">
            {gi > 0 && <div className="ti-cf-or-separator">{t('commodities.tradeImport.conditional.or')}</div>}
            <div className="ti-skip-group-card">
              <div className="ti-skip-group-header">
                <span className="ti-skip-badge">SKIP</span>
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => removeSkipRule(gi)}
                  title={t('commodities.tradeImport.conditional.remove')}
                >×</button>
              </div>
              {group.conditions.map((cond, ci) => (
                <div key={ci} className="ti-cf-condition-row">
                  {ci > 0 && <div className="ti-cf-and-label">{t('commodities.tradeImport.conditional.and')}</div>}
                  <select
                    className="form-select form-select-sm"
                    value={cond.field || ''}
                    onChange={(e) => updateSkipCondition(gi, ci, { field: e.target.value })}
                  >
                    <option value="">{t('commodities.tradeImport.conditional.selectField')}</option>
                    {sampleHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    value={cond.operator || 'IS_NULL'}
                    onChange={(e) => updateSkipCondition(gi, ci, { operator: e.target.value })}
                  >
                    {CONDITIONAL_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{t(op.labelKey)}</option>
                    ))}
                  </select>
                  {!['IS_NULL', 'IS_NOT_NULL'].includes(cond.operator) && (
                    <input
                      type="text"
                      className="form-input form-input-sm ti-config-input"
                      value={cond.value || ''}
                      onChange={(e) => updateSkipCondition(gi, ci, { value: e.target.value })}
                      placeholder={t('commodities.tradeImport.conditional.conditionValue')}
                    />
                  )}
                  {group.conditions.length > 1 && (
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => removeSkipCondition(gi, ci)}
                      title={t('commodities.tradeImport.conditional.remove')}
                    >×</button>
                  )}
                </div>
              ))}
              <button
                className="btn btn-link btn-sm ti-cf-add-btn"
                onClick={() => addSkipCondition(gi)}
              >+ {t('commodities.tradeImport.conditional.addAnd')}</button>
            </div>
          </div>
        ))}

        <button
          className="btn btn-secondary btn-sm"
          onClick={addSkipRule}
        >
          + {t('commodities.tradeImport.addSkipRule')}
        </button>
      </div>
      )}

      {/* Default Values — for fields not in the source file */}
      {(sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.defaultValues')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.defaultValuesDescription')}</p>

        {defaultValues.length > 0 && (
          <table className="ti-table">
            <thead>
              <tr>
                <th>{t('commodities.tradeImport.defaultField')}</th>
                <th>{t('commodities.tradeImport.defaultValue')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {defaultValues.map((dv, idx) => {
                const usedFields = new Set(defaultValues.filter((_, i) => i !== idx).map((d) => d.field));
                return (
                  <tr key={idx}>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={dv.field}
                        onChange={(e) => updateDefaultValue(idx, 'field', e.target.value)}
                      >
                        <option value="">{t('commodities.tradeImport.selectTargetField')}</option>
                        {mappableTargetFields
                          .filter((f) => !usedFields.has(f.value))
                          .map((f) => (
                            <option key={f.value} value={f.value} disabled={f.calculated}>{f.label}{f.calculated ? ` (${t('commodities.tradeImport.calculated')})` : ''}</option>
                          ))}
                      </select>
                    </td>
                    <td>
                      {dv.field && fieldEnums[dv.field] ? (
                        <select
                          className="form-select form-select-sm"
                          value={dv.value}
                          onChange={(e) => updateDefaultValue(idx, 'value', e.target.value)}
                        >
                          <option value="">--</option>
                          {fieldEnums[dv.field].map((ev) => (
                            <option key={ev.value} value={ev.value}>{ev.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-input form-input-sm"
                          value={dv.value}
                          onChange={(e) => updateDefaultValue(idx, 'value', e.target.value)}
                          placeholder={t('commodities.tradeImport.defaultValue')}
                        />
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => removeDefaultValue(idx)}
                        title={t('common.remove')}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <button className="btn btn-secondary btn-sm" onClick={addDefaultValue}>
          + {t('commodities.tradeImport.addDefault')}
        </button>
      </div>
      )}

      {/* Deferred Fields — fields that skip required validation during import */}
      {/* Not applicable for Expiry configs — expiry import has no deferred enrichment flow */}
      {importContext === 'Subscription' && (sampleHeaders.length > 0 || columnMappings.length > 0) && (
      <div className="ti-section">
        <h4 className="ti-section-title">{t('commodities.tradeImport.deferredFields')}</h4>
        <p className="ti-section-description">{t('commodities.tradeImport.deferredFieldsDescription')}</p>

        <div className="ti-deferred-fields-checklist">
          {targetFields.map((f) => {
            const hasDefault = defaultValues.some((dv) => dv.field === f.value && dv.value !== '');
            const isDisabled = f.calculated || hasDefault;
            const hint = f.calculated
              ? ` (${t('commodities.tradeImport.calculated')})`
              : hasDefault
                ? ` (${t('commodities.tradeImport.hasDefault')})`
                : '';
            return (
              <label key={f.value} className={`ti-deferred-field-item${isDisabled ? ' ti-deferred-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={deferredFields.includes(f.value)}
                  disabled={isDisabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDeferredFields([...deferredFields, f.value]);
                    } else {
                      setDeferredFields(deferredFields.filter((df) => df !== f.value));
                    }
                  }}
                />
                <span>{f.label}{hint}</span>
              </label>
            );
          })}
        </div>
      </div>
      )}

      {/* Actions */}
      <div className="ti-actions">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('commodities.tradeImport.saving') : t('commodities.tradeImport.save')}
        </button>

        <button
          className="btn btn-secondary"
          onClick={handleBackToList}
        >
          {t('common.cancel')}
        </button>

        {mode === 'edit' && editingId && (
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {t('commodities.tradeImport.deleteConfig')}
          </button>
        )}
      </div>

      </>
    )}

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default TradeImportConfigPanel;
